import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle, Crown } from 'lucide-react';

import { cancelarSecretario, contratarSecretario } from '@/api/client';
import {
  addonDisponivel,
  precoDoAddon,
  useOfertaSecretario,
  type Intervalo,
} from '@/components/assinatura/SecretarioAddonCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import type { SubscriptionData } from '@/hooks/useSubscription';
import { notifyMutationError } from '@/lib/errors';
import { formatDate, formatPrice } from '@/lib/formatters';

/** Quem vai assinar pelo checkout: o add-on entra junto (ver ``SubscriptionPage``). */
export function vaiPeloCheckout(subscription: SubscriptionData | null | undefined): boolean {
  return !subscription?.gateway_subscription_id && !subscription?.cortesia;
}

/**
 * O Secretário de IA como card irmão dos planos, na mesma grade e no mesmo molde
 * (docs/PLANO_SECRETARIO_ADDON.md). A situação do usuário vai no selo do canto e no
 * rodapé, como o "Plano atual" e o "Cortesia ativa" dos planos:
 *
 * - cortesia com o add-on: "Incluído"; cortesia sem ele: não há o que pagar aqui;
 * - contratado: cancelar (sem crédito; vale até o fim do período já pago);
 * - cancelado e ainda no período pago: até quando vale, e manter (desfaz o cancelamento);
 * - assinatura paga sem o add-on: clicar seleciona, e o botão adiciona (com
 *   confirmação do valor proporcional);
 * - vai assinar pelo checkout: "Incluir ao assinar" (``incluirNoCheckout`` fica com
 *   a página, que abre o checkout);
 * - assinatura no Stripe que não está paga (cancelada, vencida): reativar o plano.
 */
export function SecretarioAddonPlanCard({
  subscription,
  intervalo,
  stripeReady,
  incluirNoCheckout,
  onIncluirNoCheckoutChange,
}: {
  subscription: SubscriptionData | null | undefined;
  /** Intervalo escolhido no seletor de planos (vale para quem ainda vai assinar). */
  intervalo: Intervalo;
  stripeReady: boolean;
  incluirNoCheckout: boolean;
  onIncluirNoCheckoutChange: (v: boolean) => void;
}) {
  const { data: oferta } = useOfertaSecretario();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [selecionado, setSelecionado] = useState(false);
  const [confirmando, setConfirmando] = useState<'contratar' | 'cancelar' | 'manter' | null>(null);
  const [enviando, setEnviando] = useState(false);

  if (!oferta) return null;

  // O pessoal: os dos escritórios que a conta paga têm o card próprio.
  const addon = subscription?.addons?.find((a) => a.codigo === 'secretario' && !a.escritorio_id);
  const assinaturaPaga =
    !!subscription?.gateway_subscription_id && ['ATIVA', 'PAST_DUE'].includes(subscription.status);
  // Quem já paga é cobrado no intervalo do plano dele (é o que o backend usa); quem
  // vai assinar, no que escolheu no seletor.
  const intervaloCobrado: Intervalo = assinaturaPaga
    ? subscription?.plano?.intervalo === 'ANUAL'
      ? 'ANUAL'
      : 'MENSAL'
    : intervalo;
  const disponivel = stripeReady && addonDisponivel(oferta, intervaloCobrado);
  // Com a fatura do plano em aberto, o backend recusa: o add-on entraria sem pagamento.
  const emAtraso = subscription?.status === 'PAST_DUE';
  const podeAdicionar = !addon && assinaturaPaga && !emAtraso && disponivel;
  const centavos = intervaloCobrado === 'ANUAL' ? oferta.preco_anual_centavos : oferta.preco_mensal_centavos;

  const executar = async () => {
    const acao = confirmando;
    setEnviando(true);
    try {
      if (acao === 'contratar') {
        await contratarSecretario();
        showSuccess('Secretário de IA adicionado à sua assinatura');
      } else if (acao === 'manter') {
        await contratarSecretario();
        showSuccess('O Secretário de IA continua na sua assinatura');
      } else {
        await cancelarSecretario();
        showSuccess('Secretário de IA cancelado');
      }
      setSelecionado(false);
      await queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      await queryClient.invalidateQueries({ queryKey: ['secretario'] });
    } catch (err) {
      notifyMutationError(err, showError);
    } finally {
      setEnviando(false);
      setConfirmando(null);
    }
  };

  const rodapeTexto = (icone: ReactNode, texto: string) => (
    <p className="mt-auto flex items-center gap-1 text-xs text-[var(--color-text-tertiary)]">
      {icone}
      {texto}
    </p>
  );

  let selo: ReactNode = (
    <Badge variant="secondary" className="absolute right-3 top-3">
      Adicional
    </Badge>
  );
  let rodape: ReactNode;
  if (addon?.origem === 'CORTESIA') {
    selo = (
      <Badge variant="outline" className="absolute right-3 top-3">
        Incluído
      </Badge>
    );
    rodape = rodapeTexto(<Crown className="h-3 w-3" />, 'Cortesia ativa — sem cobrança');
  } else if (addon?.origem === 'STRIPE' && addon.expira_em) {
    // Cancelado pelo usuário: sem item no Stripe, vale até o fim do período pago.
    selo = (
      <Badge variant="outline" className="absolute right-3 top-3">
        Cancelado
      </Badge>
    );
    rodape = (
      <div className="mt-auto flex flex-col gap-2">
        {rodapeTexto(<Calendar className="h-3 w-3" />, `Vale até ${formatDate(addon.expira_em)}`)}
        <Button variant="outline" className="w-full" disabled={!stripeReady} onClick={() => setConfirmando('manter')}>
          Manter o Secretário
        </Button>
      </div>
    );
  } else if (addon) {
    selo = (
      <Badge variant="outline" className="absolute right-3 top-3">
        Contratado
      </Badge>
    );
    rodape = (
      <div className="mt-auto flex flex-col gap-2">
        {rodapeTexto(<Calendar className="h-3 w-3" />, 'Cobrado junto com o seu plano')}
        <Button variant="outline" className="w-full" disabled={!stripeReady} onClick={() => setConfirmando('cancelar')}>
          Cancelar o Secretário
        </Button>
      </div>
    );
  } else if (subscription?.cortesia && !assinaturaPaga) {
    rodape = rodapeTexto(null, 'Sua cortesia não inclui o Secretário. Para tê-lo, fale com o suporte.');
  } else if (assinaturaPaga) {
    rodape = !disponivel ? (
      rodapeTexto(null, 'Disponível em breve')
    ) : emAtraso ? (
      rodapeTexto(null, 'Regularize o pagamento da assinatura para contratar o Secretário.')
    ) : selecionado ? (
      <Button
        className="mt-auto w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary)]/90"
        onClick={(e) => {
          e.stopPropagation();
          setConfirmando('contratar');
        }}
      >
        Adicionar à assinatura
      </Button>
    ) : (
      <div className="mt-auto w-full py-2 text-center text-sm text-muted-foreground">Clique para adicionar</div>
    );
  } else if (vaiPeloCheckout(subscription)) {
    rodape = (
      <div className="mt-auto flex flex-col gap-2">
        <p className="text-xs text-[var(--color-text-tertiary)]">
          {subscription?.escritorio_que_paga
            ? 'O Secretário não vem com a vaga do escritório. Para tê-lo, assine um plano seu.'
            : 'No período de avaliação o Secretário já vem com 5 triagens. Ao assinar sem ele, os casos novos passam a chegar só com o nome e o assunto.'}
        </p>
        {disponivel ? (
          <div className="flex min-h-11 items-center gap-2">
            <Switch id="incluir-secretario" checked={incluirNoCheckout} onCheckedChange={onIncluirNoCheckoutChange} />
            <Label htmlFor="incluir-secretario" className="cursor-pointer text-sm">
              Incluir ao assinar
            </Label>
          </div>
        ) : (
          <p className="text-xs text-[var(--color-text-tertiary)]">Disponível em breve</p>
        )}
      </div>
    );
  } else {
    // Já teve assinatura no Stripe e ela não está paga (cancelada, expirada): a
    // escolha do plano reativa pela troca de plano, que não leva o add-on junto.
    rodape = rodapeTexto(null, 'Reative um plano para contratar o Secretário.');
  }

  const itens = [`${oferta.atendimentos_max} triagens por mês`, 'Atualizações de processo ilimitadas'];

  return (
    <>
      <Card
        onClick={podeAdicionar ? () => setSelecionado((v) => !v) : undefined}
        className={`relative flex flex-col overflow-hidden transition-all ${podeAdicionar ? 'cursor-pointer' : ''} ${
          selecionado
            ? 'ring-2 ring-[var(--color-primary)] border-[var(--color-primary)] shadow-lg shadow-[var(--color-primary)]/10'
            : addon
              ? 'border-muted-foreground/30 bg-muted/30'
              : 'hover:border-[var(--color-primary)]/30'
        }`}
      >
        {selo}
        <CardContent className="flex flex-1 flex-col p-5">
          <h3 className="mb-1 text-base font-semibold text-[var(--color-text-primary)]">Secretário de IA</h3>
          <div className="mb-3">
            <span className="text-2xl font-bold text-[var(--color-text-primary)]">+ {formatPrice(centavos)}</span>
            <span className="text-sm text-[var(--color-text-secondary)]">
              /{intervaloCobrado === 'ANUAL' ? 'ano' : 'mês'}
            </span>
          </div>
          <p className="mb-3 text-xs text-[var(--color-text-secondary)]">
            Atende seus clientes pelo WhatsApp: acolhe os casos novos, recolhe os documentos e responde quem pergunta
            do processo.
          </p>
          <div className="mb-4 flex flex-col gap-1.5">
            {itens.map((item) => (
              <div key={item} className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-500" />
                <span className="text-xs leading-snug text-[var(--color-text-primary)]">{item}</span>
              </div>
            ))}
          </div>
          {rodape}
        </CardContent>
      </Card>
      <ConfirmDialog
        open={confirmando === 'contratar'}
        title="Adicionar o Secretário de IA?"
        message={`O valor de ${precoDoAddon(oferta, intervaloCobrado)} é cobrado agora, proporcional ao tempo até a próxima fatura do seu plano.`}
        confirmLabel={enviando ? 'Adicionando…' : 'Adicionar'}
        loading={enviando}
        onConfirm={executar}
        onCancel={() => setConfirmando(null)}
      />
      <ConfirmDialog
        open={confirmando === 'cancelar'}
        title="Cancelar o Secretário de IA?"
        message="Ele continua até o fim do período já pago, sem crédito do tempo restante. Depois, os casos novos passam a chegar só com o nome e o assunto."
        confirmLabel={enviando ? 'Cancelando…' : 'Cancelar o Secretário'}
        cancelLabel="Manter"
        confirmColor="error"
        loading={enviando}
        onConfirm={executar}
        onCancel={() => setConfirmando(null)}
      />
      <ConfirmDialog
        open={confirmando === 'manter'}
        title="Manter o Secretário de IA?"
        message="O cancelamento é desfeito, e ele volta a ser cobrado junto com o seu plano."
        confirmLabel={enviando ? 'Mantendo…' : 'Manter'}
        cancelLabel="Voltar"
        loading={enviando}
        onConfirm={executar}
        onCancel={() => setConfirmando(null)}
      />
    </>
  );
}
